// lib/operateur-feuille.js — LE COMPOSEUR DE FEUILLES : une description entre,
// une feuille prête à afficher sort — ou un refus qui dit pourquoi.
//
// ══ LE CONSTAT QUI COMMANDE TOUT (Dimitri, 2026-09-13) ═════════════════════
//
//   « En prod tout ouvre un écran, chez moi ça ouvre une feuille. C'est ça
//     qu'il faut combler. » Et : « Mes quatre feuilles abouties font foi, pas
//     la moyenne. »
//
// Dans sa maquette (docs/handoff/admin-proto.html), 21 événements sur 30
// ouvrent une feuille. Le modèle complet n'existe que sur quatre d'entre
// elles — feuilleCaution, feuillePaiement, feuilleTrou, feuilleRetouches — et
// ce fichier est ce qu'elles ont en commun, une fois le contenu retiré. Car le
// cartouche est formel : AUCUNE de leurs phrases ne doit se retrouver en dur
// ici. Ce module ne compose aucune prose : il reçoit une DESCRIPTION (ce qu'un
// lecteur a produit sur la situation du moment), la VÉRIFIE contre les cinq
// règles, et la RANGE dans les neuf blocs.
//
// ══ LES NEUF BLOCS, TIRÉS DES QUATRE FEUILLES ══════════════════════════════
//
//   fait     — chiffré et SOURCÉ (d'où vient le nombre, qui l'a produit)
//   enjeu    — ce que ça coûte OU ce que ça bloque ; jamais « c'est important »
//   systeme  — ce que le système fait déjà seul, daté. ⚠️ C'EST CE BLOC QUI
//              DÉCIDE S'IL Y A UN GESTE.
//   regarde  — ce qu'il a regardé à côté, et ce qu'il a éliminé
//   avis     — l'avis tranché (« à votre place je ferais X »)
//   gestes   — ceux qui agissent, ceux qui attendent une connexion (gris,
//              motivés), la navigation en second rang
//   pari     — chiffré, avec son seuil d'erreur
//   base     — sur combien de données il se prononce, et si c'est peu
//   aveugle  — ce qu'il ne voit pas
//
// Une partie sans matière ne s'affiche pas : c'est `lib/analyse-gabarit.js`
// (`rangerLAnalyse`, liste `PARTIES_FEUILLE`) qui le fait — étendu, pas
// doublé.
//
// ══ LES CINQ RÈGLES DE DIMITRI — CHACUNE A SON TEST QUI PEUT ROUGIR ════════
//
//   1 · JAMAIS UN GESTE QUE LE SYSTÈME FAIT DÉJÀ. La description déclare
//       `automate: <clé>|null` EXPLICITEMENT (une clé absente n'est pas un
//       null : c'est un oubli, et la feuille est refusée). Si la clé désigne
//       un automate qui EXISTE dans `lib/operateur-automates.js` (la table de
//       vérité, prouvée par son propre test), tout geste qui agit est REFUSÉ
//       et remplacé par « on attend », avec un RENDEZ-VOUS DATÉ obligatoire.
//   2 · NI CAUSE DEVINÉE CHEZ UN VOYAGEUR, NI PROMESSE. Tout texte destiné à
//       un tiers passe un filtre déterministe (même famille que
//       `looksLikeInternalMechanics`) ; un texte qui devine ou promet fait
//       refuser le geste qui le porte.
//   3 · JAMAIS UN PRIX PLAT SUR UNE COURBE. Un geste de prix unique sur un
//       intervalle où la courbe porte plusieurs prix distincts est refusé —
//       et si la courbe n'est pas fournie, il est refusé aussi (on ne peut
//       pas prouver qu'elle est plate). Le geste juste est un POURCENTAGE,
//       qui se déplie en un prix PAR DATE — la forme de la courbe est gardée.
//   4 · DIRE SA BASE, ET QUAND ELLE EST FAIBLE. `base` non nulle obligatoire ;
//       sous le seuil, la phrase le dit.
//   5 · AUCUN BOUTON MORT. Chaque bouton est un levier du registre avec ses
//       arguments permis, OU une navigation vers un écran de la carte / une
//       vue BRANCHÉE, OU un gris MOTIVÉ. Jamais les trois nuls : ce qui ne
//       passe pas est rangé dans `gestes.refuses`, avec sa raison, et ne
//       devient jamais un bouton.
//
// ══ CE QUE CE MODULE NE FAIT PAS ════════════════════════════════════════════
//
// · Il n'exécute rien, ne lit aucune table, n'appelle aucun réseau. Le clic
//   passe par `src/lib/geste-executer.js`, avec le jeton de Dimitri.
// · Il ne câble aucune source d'événement : une source qui veut une feuille
//   pose une `description` sur son événement ; c'est tout.
// · Il n'invente pas de contenu : un défaut de description se DIT (`defauts`)
//   et la feuille n'est pas affichée. Fail-closed, jamais une feuille à
//   moitié vraie.
//
// ⚠️ MIROIR `src/lib/operateur-feuille.js` OCTET-IDENTIQUE (ADR-021). Les
// quatre `require` ci-dessous sont RELATIFS et visent des modules qui existent
// aux deux endroits, eux aussi en miroir ; `operateur-feuille-parite.test.js`
// compare les cinq fichiers deux à deux.

const {
  rangerLAnalyse,
  PARTIES_FEUILLE,
  phraseDeLAveugle,
} = require("./analyse-gabarit.js");
const { automate: lireAutomate } = require("./operateur-automates.js");
const { lireLevier, filtrerArgs } = require("./operateur-leviers.js");
const { lireVue, filtrerArgsVue } = require("./vues-registre.js");
const { lireEcran } = require("./app-carte.js");

// ── Les bornes ──────────────────────────────────────────────────────────────

/** Sous ce nombre de données, la base est FAIBLE et la phrase doit le dire. */
const SEUIL_BASE_FAIBLE = 30;
/** Un motif de gris doit expliquer, pas décorer. */
const MOTIF_MIN_CARS = 12;
/** Les leviers dont un prix unique sur plusieurs dates est un prix plat. */
const LEVIERS_PRIX_PLAT = Object.freeze({
  pricing_set_manual: "price_eur",
  pricing_apply_base: "base_price",
});
/** Le levier par lequel un pourcentage se déplie, date par date. */
const LEVIER_PAR_DATE = "pricing_set_manual";

// ── Règle 2 : le filtre des textes destinés à un tiers ──────────────────────
//
// Même famille que `looksLikeInternalMechanics` (lib/inbox-ai-handlers.js) :
// une expression régulière fermée, en FR/EN, qui peut ROUGIR. Deux
// familles, nommées pour que le refus dise laquelle a parlé.

const CAUSE_DEVINEE_RE = new RegExp(
  [
    // « vous avez sans doute / probablement / peut-être / certainement oublié »
    "\\bvous (avez|aviez|n['’]avez)\\s+(sans doute|probablement|peut-être|peut être|certainement|sûrement|surement)\\b",
    "\\b(sans doute|probablement|certainement|sûrement|surement)\\s+(vous|votre|vos)\\b",
    "\\bpeut-être que vous\\b",
    // « vous avez dû oublier / rater »
    "\\bvous (avez|aviez) dû\\b",
    // « je suppose / j'imagine que vous »
    "\\b(je suppose|j['’]imagine|je devine|je pense) que (vous|votre|vos)\\b",
    "\\bil semble que (vous|votre|vos)\\b",
    "\\b(votre|vos) (carte|banque|téléphone|telephone|mail|e-mail|email)s? (est|sont|doit|doivent|a|ont) (sans doute|probablement|peut-être|certainement|sûrement)\\b",
    // EN
    "\\byou (probably|must have|may have|might have|likely)\\b",
    "\\bi (guess|suppose|assume|imagine) (you|your)\\b",
  ].join("|"),
  "i",
);

const PROMESSE_RE = new RegExp(
  [
    "\\bje (vous )?(promets|garantis|m['’]engage)\\b",
    "\\bc['’]est (promis|garanti)\\b",
    "\\b(promis|garanti|assuré)\\s*[.!]",
    "\\bvous (serez|allez être) rembours",
    "\\b(on|nous) vous rembours",
    "\\bsera rembours",
    "\\bvous (aurez|recevrez|obtiendrez)\\b[^.!?\\n]*\\b(sans faute|à coup sûr|a coup sur|quoi qu['’]il arrive|garanti)\\b",
    "\\b(aucun|pas de) (problème|souci|risque)\\b[^.!?\\n]*\\b(garanti|promis)\\b",
    // EN
    "\\bi (promise|guarantee)\\b",
    "\\byou (will|'ll) (definitely|certainly) (get|receive|be refunded)\\b",
    "\\b(full|guaranteed) refund\\b",
  ].join("|"),
  "i",
);

/**
 * `null` si le texte peut partir chez un tiers ; sinon la famille qui a
 * parlé (`cause_devinee` | `promesse`).
 */
function texteTiersRefuse(texte) {
  if (typeof texte !== "string" || !texte.trim()) return null;
  if (CAUSE_DEVINEE_RE.test(texte)) return "cause_devinee";
  if (PROMESSE_RE.test(texte)) return "promesse";
  return null;
}

// ── Petits outils ───────────────────────────────────────────────────────────

const _str = (v) => (typeof v === "string" ? v.trim() : "");
const _dateOk = (v) =>
  typeof v === "string" &&
  v.length >= 10 &&
  !Number.isNaN(new Date(v.length === 10 ? `${v}T12:00:00Z` : v).getTime());
const _jourDe = (v) => String(v || "").slice(0, 10);

/** Les prix DISTINCTS que la courbe porte entre deux dates (bornes incluses). */
function _prixSurCourbe(courbe, du, au) {
  const d1 = _jourDe(du);
  const d2 = _jourDe(au);
  const prix = new Set();
  for (const p of Array.isArray(courbe) ? courbe : []) {
    if (!p || !_dateOk(p.date)) continue;
    const j = _jourDe(p.date);
    if (j < d1 || j > d2) continue;
    if (Number.isFinite(Number(p.prix))) prix.add(Number(p.prix));
  }
  return [...prix];
}

// ── Les gestes, un par un ───────────────────────────────────────────────────

/** Un levier vérifié contre le registre (règle 5), ou un refus. */
function _levierVerifie(g) {
  const nom = _str(g.levier);
  const l = lireLevier(nom);
  if (!l)
    return { refus: `levier « ${nom || "?"} » absent du registre`, regle: 5 };
  const args = filtrerArgs(nom, g.args || {});
  if (args === null)
    return {
      refus: `levier « ${nom} » : un argument n'est pas dans sa liste permise`,
      regle: 5,
    };
  return { levier: nom, args, description: l };
}

/**
 * Ce que la carte (`CarteGesteDemande`) lit sur un geste, recopié DU REGISTRE
 * — jamais de la description : le titre et le constatable sont ceux du levier.
 */
function _champsDeCarte(l) {
  return {
    titre: l.titre,
    constatable: l.constatable,
    endpoint: l.endpoint,
    marquages: l.marquages || [],
    motif_requis: l.motif_requis === true,
    motif_champ: l.motif_champ || undefined,
    sans_retour: l.sans_retour || null,
    args_choisis: l.args_choisis || undefined,
  };
}

/** Les textes de ce geste qui partent chez un tiers. */
function _textesTiers(g, l) {
  const out = [];
  if (g.tiers && typeof g.tiers === "object" && _str(g.tiers.texte))
    out.push(g.tiers.texte);
  // Le registre dit quels arguments sont du texte ; ceux d'un levier qui SORT
  // de chez FBZ vers un voyageur (famille messages / voyageurs) vont chez un
  // tiers par construction.
  //
  // ⚠️ DETTE ÉCRITE ET DATÉE — LE FILTRE NE COUVRE QU'UNE FAMILLE SUR TROIS
  // (2026-09-21, relevé au registre, pas déduit). La liste ci-dessous est un
  // raccourci qui ne tient plus :
  //   · `voyageurs` (5 leviers) n'a AUCUN `args_textes` — l'entrée est morte ;
  //   · `livret` (27 leviers, `ou_ca_part: page_publique`) est ABSENT : un
  //     `content` deviné ou promettant passerait sur `livret_module_editer`,
  //     qui n'a pas déclaré `content` dans ses `args_textes` — le trou est
  //     donc double (famille non filtrée ET texte non déclaré) ;
  //   · `menage_run_champ_set` (`ou_ca_part: agent`) et
  //     `data_ajustement_ajouter` (`atteignable`) portent un `args_textes` que
  //     rien ne relit non plus.
  // Aujourd'hui LATENT : `inbox_reply` (famille `messages`) est le seul levier
  // à la fois filtré et porteur de texte, et aucune source ne compose encore
  // de geste `livret` / `menage` / `voyageurs` dans une feuille.
  // LA PISTE : filtrer sur `ou_ca_part` qui SORT de chez FBZ (voyageur,
  // proprietaire, agent, page_publique) plutôt que sur une liste de familles —
  // et fermer l'autre moitié du trou en exigeant qu'un argument de texte libre
  // soit déclaré `args_textes`. DEVIENT BLOQUANT à la première feuille qui
  // porte un geste `livret`, `menage` ou `voyageurs`.
  if (
    l &&
    Array.isArray(l.args_textes) &&
    ["messages", "voyageurs"].includes(l.famille)
  )
    for (const c of l.args_textes)
      if (_str((g.args || {})[c])) out.push(g.args[c]);
  return out;
}

/**
 * Compose UN geste. Rend `{ok:true, geste}` ou `{ok:false, refus, regle}`.
 * `ctx.courbe` sert la règle 3 ; `ctx.automate` la règle 1.
 */
function _composerGeste(g, ctx) {
  if (!g || typeof g !== "object")
    return { ok: false, refus: "geste illisible", regle: 5 };
  const libelle = _str(g.libelle);
  const type = _str(g.type);

  if (type === "attend") {
    const motif = _str(g.motif);
    if (!libelle || motif.length < MOTIF_MIN_CARS)
      return {
        ok: false,
        libelle,
        refus: "un gris sans motif est un bouton mort",
        regle: 5,
      };
    return { ok: true, geste: { type, libelle, motif, sous: _str(g.sous) } };
  }

  if (type === "navigation") {
    if (!libelle)
      return { ok: false, refus: "navigation sans libellé", regle: 5 };
    if (_str(g.ecran)) {
      const e = lireEcran(_str(g.ecran));
      if (!e)
        return {
          ok: false,
          libelle,
          refus: `écran « ${_str(g.ecran)} » inconnu de la carte`,
          regle: 5,
        };
      const cible =
        g.cible == null || String(g.cible).length > 60 ? null : String(g.cible);
      return {
        ok: true,
        geste: {
          type,
          libelle,
          ecran: _str(g.ecran),
          cible,
          sous: _str(g.sous),
        },
      };
    }
    if (_str(g.vue)) {
      const v = lireVue(_str(g.vue));
      if (!v || v.branchee !== true)
        return {
          ok: false,
          libelle,
          refus: `vue « ${_str(g.vue)} » inconnue ou non branchée`,
          regle: 5,
        };
      const args = filtrerArgsVue(_str(g.vue), g.args || {});
      if (args === null)
        return {
          ok: false,
          libelle,
          refus: `vue « ${_str(g.vue)} » : argument hors liste`,
          regle: 5,
        };
      return {
        ok: true,
        geste: {
          type,
          libelle,
          vue: _str(g.vue),
          args,
          titre: v.titre,
          forme: v.forme,
          sous: _str(g.sous),
        },
      };
    }
    return {
      ok: false,
      libelle,
      refus: "navigation sans écran ni vue",
      regle: 5,
    };
  }

  if (type === "pourcentage") {
    // Règle 3, côté constructif : le pourcentage se déplie en UN prix PAR
    // DATE, sur la courbe fournie. Sans courbe, rien à déplier — refus.
    const pct = Number(g.pct);
    if (!libelle || !Number.isFinite(pct) || pct === 0 || Math.abs(pct) >= 100)
      return { ok: false, libelle, refus: "pourcentage illisible", regle: 3 };
    if (!_dateOk(g.du) || !_dateOk(g.au) || _jourDe(g.du) > _jourDe(g.au))
      return { ok: false, libelle, refus: "intervalle illisible", regle: 3 };
    const dates = (Array.isArray(ctx.courbe) ? ctx.courbe : []).filter(
      (p) =>
        p &&
        _dateOk(p.date) &&
        _jourDe(p.date) >= _jourDe(g.du) &&
        _jourDe(p.date) <= _jourDe(g.au) &&
        Number.isFinite(Number(p.prix)) &&
        p.reserve !== true,
    );
    if (!dates.length)
      return {
        ok: false,
        libelle,
        refus: "aucune date libre sur la courbe pour cet intervalle",
        regle: 3,
      };
    if (ctx.automate) return _refusAutomate(libelle, ctx);
    const lot = [];
    for (const p of dates) {
      const jour = _jourDe(p.date);
      const prix = Math.round(Number(p.prix) * (1 + pct / 100));
      const v = _levierVerifie({
        levier: LEVIER_PAR_DATE,
        args: {
          logement_id: g.logement_id,
          date_from: jour,
          date_to: jour,
          price_eur: prix,
          raison: `${pct > 0 ? "+" : ""}${pct} % sur la courbe`,
        },
      });
      if (v.refus) return { ok: false, libelle, refus: v.refus, regle: 5 };
      lot.push({
        ..._champsDeCarte(v.description),
        levier: v.levier,
        args: v.args,
        avant: Number(p.prix),
        apres: prix,
        // ⚠️ CHEMIN ARGENT — LES SOUS-GESTES PORTENT LE MARQUEUR, COMME LES
        // AUTRES (2026-09-21). `src/lib/geste-executer.js` est fail-closed :
        // un geste qui ne DIT PAS qu'il est ouvert est refusé (« rechargez
        // l'écran, rien n'a été fait »). La branche `levier` recopiait le
        // champ ; celle-ci l'oubliait — et le lot que la règle 3 exige (un
        // pourcentage vaut mieux qu'un prix plat) rendait donc des sous-cartes
        // TOUTES grises. La règle 3 violait la règle 5.
        //
        // ⚠️ RECOPIE À TROIS ÉTATS, JAMAIS `=== true` : un marqueur absent doit
        // le RESTER (« ce geste ne dit pas… , rechargez »), et non devenir un
        // « pas encore ouvert » qui enverrait chercher la mauvaise chose.
        geste_ouvert:
          typeof g.geste_ouvert === "boolean" ? g.geste_ouvert : undefined,
        // L'heure de composition voyage avec le geste, comme le marqueur : un
        // sous-geste qui la perdrait serait le seul, dans le lot, sans âge.
        compose_at: typeof g.compose_at === "string" ? g.compose_at : undefined,
      });
    }
    return {
      ok: true,
      geste: { type: "lot", libelle, pct, sous: _str(g.sous), gestes: lot },
    };
  }

  if (type === "levier") {
    if (!libelle) return { ok: false, refus: "geste sans libellé", regle: 5 };
    const v = _levierVerifie(g);
    if (v.refus) return { ok: false, libelle, refus: v.refus, regle: v.regle };

    // Règle 2 — les textes qui partent chez un tiers.
    for (const t of _textesTiers(g, v.description)) {
      const famille = texteTiersRefuse(t);
      if (famille)
        return {
          ok: false,
          libelle,
          refus:
            famille === "cause_devinee"
              ? "le texte devine une cause chez le voyageur"
              : "le texte promet ce qu'on ne tient pas",
          regle: 2,
        };
    }

    // Règle 3 — un prix unique sur un intervalle qui porte une courbe.
    const champPrix = LEVIERS_PRIX_PLAT[v.levier];
    if (champPrix) {
      const du = v.args.date_from;
      const au = v.args.date_to;
      if (_dateOk(du) && _dateOk(au) && _jourDe(du) !== _jourDe(au)) {
        const prix = _prixSurCourbe(ctx.courbe, du, au);
        if (!Array.isArray(ctx.courbe))
          return {
            ok: false,
            libelle,
            refus:
              "prix unique sur plusieurs dates sans la courbe : impossible de prouver qu'elle est plate",
            regle: 3,
          };
        if (prix.length > 1)
          return {
            ok: false,
            libelle,
            refus: `prix plat sur une courbe (${prix.length} prix distincts du ${_jourDe(du)} au ${_jourDe(au)}) — c'est un pourcentage`,
            regle: 3,
          };
      }
    }

    // Règle 1 — le système le fait déjà.
    if (ctx.automate) return _refusAutomate(libelle, ctx);

    return {
      ok: true,
      geste: {
        ..._champsDeCarte(v.description),
        type,
        libelle,
        sous: _str(g.sous),
        levier: v.levier,
        args: v.args,
        geste_ouvert:
          typeof g.geste_ouvert === "boolean" ? g.geste_ouvert : undefined,
        // L'heure de composition voyage avec le geste, comme le marqueur : un
        // sous-geste qui la perdrait serait le seul, dans le lot, sans âge.
        compose_at: typeof g.compose_at === "string" ? g.compose_at : undefined,
      },
    };
  }

  return {
    ok: false,
    libelle,
    refus: `type de geste « ${type || "?"} » inconnu`,
    regle: 5,
  };
}

function _refusAutomate(libelle, ctx) {
  return {
    ok: false,
    libelle,
    refus: `le système le fait déjà (${ctx.automate.cle}) : ${ctx.automate.phrase}`,
    regle: 1,
  };
}

// ── Le composeur ────────────────────────────────────────────────────────────

/**
 * @typedef {object} DescriptionFeuille
 * @property {string} titre
 * @property {{texte:string, source:string, chiffre?:{valeur:number, unite:string}}} fait
 * @property {{cout?:string, bloque?:string}} enjeu — au moins l'un des deux
 * @property {string|null} automate — clé de `lib/operateur-automates.js`, ou
 *   null EXPLICITE ; la clé absente est un défaut
 * @property {{quand:string, quoi:string}} [rendezVous] — obligatoire si
 *   l'automate existe ; `quand` = ISO
 * @property {Array<{quand:string, quoi:string, bloque?:boolean}>} [prevu] — la
 *   frise de ce qui va partir tout seul
 * @property {Array<string|{texte:string, cloche?:boolean, elimine?:boolean}>} [regarde]
 * @property {string} [avis]
 * @property {Array<object>} [gestes] — `{type:'levier', levier, args, libelle, sous?, tiers?:{texte}}`
 *   | `{type:'pourcentage', pct, logement_id, du, au, libelle}`
 *   | `{type:'navigation', ecran|vue, cible?|args?, libelle}`
 *   | `{type:'attend', libelle, motif}`
 * @property {Array<{date:string, prix:number, reserve?:boolean}>} [courbe]
 * @property {{texte:string, cible:number, grandeur:string, verif_le:string, seuil_erreur:string}} [pari]
 * @property {{n:number, quoi:string, seuil?:number, reserve?:string}} base
 * @property {string|string[]} [aveugle]
 */

/**
 * Compose une feuille. Ne lance jamais.
 *
 * @param {DescriptionFeuille} d
 * @returns {{ok:boolean, defauts:string[], titre:string, blocs:string[],
 *   manquantes:string[], fait:object|null, enjeu:object|null, systeme:object|null,
 *   regarde:Array, avis:string, gestes:{agit:Array, attend:Array, navigue:Array,
 *   refuses:Array}, pari:object|null, base:object|null, aveugle:string}}
 */
function composerFeuille(d) {
  const desc = d && typeof d === "object" && !Array.isArray(d) ? d : {};
  const defauts = [];

  // Le titre.
  const titre = _str(desc.titre);
  if (!titre) defauts.push("il manque le titre de la feuille.");

  // 1 · LE FAIT — chiffré et sourcé.
  const f = desc.fait && typeof desc.fait === "object" ? desc.fait : null;
  const fait =
    f && _str(f.texte) && _str(f.source)
      ? {
          texte: _str(f.texte),
          source: _str(f.source),
          chiffre:
            f.chiffre &&
            typeof f.chiffre === "object" &&
            Number.isFinite(Number(f.chiffre.valeur))
              ? {
                  valeur: Number(f.chiffre.valeur),
                  unite: _str(f.chiffre.unite),
                }
              : null,
        }
      : null;
  if (!fait)
    defauts.push(
      "le fait : il manque son texte, ou d'où vient le chiffre (sa source).",
    );

  // 2 · L'ENJEU — coût OU blocage.
  const e = desc.enjeu && typeof desc.enjeu === "object" ? desc.enjeu : {};
  const enjeu = { cout: _str(e.cout), bloque: _str(e.bloque) };
  if (!enjeu.cout && !enjeu.bloque)
    defauts.push(
      "l'enjeu : la feuille ne dit ni ce que ça coûte, ni ce que ça bloque.",
    );

  // 3 · LE SYSTÈME — la table de vérité décide.
  let automate = null;
  if (!Object.prototype.hasOwnProperty.call(desc, "automate")) {
    defauts.push(
      "automate : non déclaré — il faut dire lequel s'en charge tout seul, ou dire explicitement qu'aucun ne s'en charge.",
    );
  } else if (desc.automate !== null) {
    const cle = _str(desc.automate);
    const entree = lireAutomate(cle);
    if (!entree)
      defauts.push(
        `automate : la référence « ${cle || "?"} » est inconnue — rien dans le système ne porte ce nom.`,
      );
    else if (entree.automate)
      automate = {
        cle,
        phrase: entree.automate.phrase,
        reserve: entree.automate.reserve || "",
      };
    // Une entrée déclarée `automate: null` dans la table = « rien ne part tout
    // seul » — c'est la raison de PROPOSER, pas de s'effacer.
    else automate = null;
  }
  let rendezVous = null;
  if (automate) {
    const rv =
      desc.rendezVous && typeof desc.rendezVous === "object"
        ? desc.rendezVous
        : null;
    if (rv && _dateOk(rv.quand) && _str(rv.quoi))
      rendezVous = { quand: rv.quand, quoi: _str(rv.quoi) };
    else
      defauts.push(
        "le système s'en charge déjà : il faut alors un rendez-vous daté — le jour, et ce qui se passera ce jour-là.",
      );
  }
  const prevu = (Array.isArray(desc.prevu) ? desc.prevu : [])
    .filter((p) => p && _dateOk(p.quand) && _str(p.quoi))
    .map((p) => ({
      quand: p.quand,
      quoi: _str(p.quoi),
      bloque: p.bloque === true,
    }));
  const systeme =
    automate || prevu.length || rendezVous
      ? { automate, rendezVous, prevu }
      : null;

  // 4 · CE QU'IL A REGARDÉ.
  const regarde = (Array.isArray(desc.regarde) ? desc.regarde : [])
    .map((r) =>
      typeof r === "string"
        ? { texte: r.trim(), cloche: false, elimine: false }
        : r && typeof r === "object"
          ? {
              texte: _str(r.texte),
              cloche: r.cloche === true,
              elimine: r.elimine === true,
            }
          : null,
    )
    .filter((r) => r && r.texte);

  // 5 · L'AVIS.
  const avis = _str(desc.avis);

  // 6 · LES GESTES.
  const ctx = { automate, courbe: desc.courbe };
  const gestes = { agit: [], attend: [], navigue: [], refuses: [] };
  for (const g of Array.isArray(desc.gestes) ? desc.gestes : []) {
    const r = _composerGeste(g, ctx);
    if (!r.ok) {
      gestes.refuses.push({
        libelle: r.libelle || "",
        motif: r.refus,
        regle: r.regle,
      });
      continue;
    }
    if (r.geste.type === "attend") gestes.attend.push(r.geste);
    else if (r.geste.type === "navigation") gestes.navigue.push(r.geste);
    else gestes.agit.push(r.geste);
  }
  // Règle 1, la moitié constructive : ce qui est refusé parce que le système
  // le fait déjà devient UN « on attend », daté.
  const refusesParAutomate = gestes.refuses.filter((r) => r.regle === 1);
  if (automate && rendezVous && refusesParAutomate.length)
    gestes.attend.unshift({
      type: "attend",
      libelle: rendezVous.quoi,
      motif: automate.phrase,
      quand: rendezVous.quand,
      remplace: refusesParAutomate.map((r) => r.libelle).filter(Boolean),
    });

  // ⚠️ UN GESTE REFUSÉ N'EST PAS UN GESTE ABSENT — ET C'ÉTAIT MUET (2026-09-21).
  // Le cartouche promettait que « ce qui ne passe pas est rangé dans
  // `gestes.refuses`, avec sa raison ». Il l'était — et PERSONNE ne le lisait :
  // `ok` restait `true`, la feuille s'affichait entière, le bouton manquait
  // sans un mot. Un motif de gris de onze caractères, douze espaces, `null` :
  // trois refus, trois feuilles d'apparence complète. C'est exactement l'échec
  // silencieux et plausible que ce module existe pour refuser.
  //
  // Une feuille qui CACHE un bouton refusé ment par omission : elle est donc
  // refusée en entier, et le défaut nomme sa règle. Seule la règle 1 est
  // épargnée — son refus est CONSTRUCTIF (le système le fait déjà), il devient
  // le « on attend » daté ci-dessus, il ne disparaît pas.
  for (const r of gestes.refuses)
    if (r.regle !== 1)
      defauts.push(
        `règle ${r.regle} — geste « ${r.libelle || "sans libellé"} » refusé : ${r.motif}`,
      );

  // 7 · LE PARI — chiffré, daté, avec son seuil d'erreur ; sinon il tombe.
  let pari = null;
  if (desc.pari && typeof desc.pari === "object") {
    const p = desc.pari;
    if (
      _str(p.texte) &&
      Number.isFinite(Number(p.cible)) &&
      _str(p.grandeur) &&
      _dateOk(p.verif_le) &&
      _str(p.seuil_erreur)
    )
      pari = {
        texte: _str(p.texte),
        cible: Number(p.cible),
        grandeur: _str(p.grandeur),
        verif_le: p.verif_le,
        seuil_erreur: _str(p.seuil_erreur),
      };
    else
      gestes.refuses.push({
        libelle: "pari",
        motif:
          "un pari sans chiffre, sans date de vérification ou sans seuil d'erreur n'est pas un pari",
        regle: 0,
      });
  }

  // 8 · LA BASE — obligatoire, et faible se dit.
  let base = null;
  const b = desc.base && typeof desc.base === "object" ? desc.base : null;
  if (b && Number.isFinite(Number(b.n)) && Number(b.n) > 0 && _str(b.quoi)) {
    const n = Number(b.n);
    const seuil =
      Number.isFinite(Number(b.seuil)) && Number(b.seuil) > 0
        ? Number(b.seuil)
        : SEUIL_BASE_FAIBLE;
    const faible = n < seuil;
    const reserve = _str(b.reserve);
    base = {
      n,
      quoi: _str(b.quoi),
      seuil,
      faible,
      phrase:
        `${n} ${_str(b.quoi)}` +
        (faible ? " — c'est peu : une estimation, pas une certitude" : "") +
        (reserve ? `. ${reserve}` : ""),
    };
  } else
    defauts.push(
      "la base : il faut dire sur combien de données la feuille se prononce (au moins une), et lesquelles.",
    );

  // 9 · L'AVEUGLE.
  const range = rangerLAnalyse(
    {
      fait,
      enjeu,
      systeme,
      regarde,
      avis,
      gestes: {
        agit: gestes.agit,
        attend: gestes.attend,
        navigue: gestes.navigue,
      },
      pari,
      base,
      aveugle: desc.aveugle,
    },
    PARTIES_FEUILLE,
  );
  const aveugle = phraseDeLAveugle(
    range.manquantes.filter((m) => ["regarde", "avis", "pari"].includes(m)),
    desc.aveugle,
  );

  return {
    ok: defauts.length === 0,
    defauts,
    titre,
    blocs: range.parties.map((p) => p.nom).concat(aveugle ? ["aveugle"] : []),
    manquantes: range.manquantes,
    fait,
    enjeu: enjeu.cout || enjeu.bloque ? enjeu : null,
    systeme,
    regarde,
    avis,
    gestes,
    pari,
    base,
    aveugle,
  };
}

module.exports = {
  composerFeuille,
  texteTiersRefuse,
  SEUIL_BASE_FAIBLE,
  MOTIF_MIN_CARS,
  LEVIERS_PRIX_PLAT,
  LEVIER_PAR_DATE,
  _internal: { CAUSE_DEVINEE_RE, PROMESSE_RE, _prixSurCourbe, _composerGeste },
};
